'use client';

import { c as _c } from "react/compiler-runtime";
import { FILE_INPUT_ACCEPT } from '@shared/all/uploadTypes';
import { calculateNewPostTronCostBreakdown } from '@shared/utils/helpers/formatNumbers';
import useUser from '@shared/utils/hooks/useUser';
import { Logo } from '@shared/utils/icons';
import type { Editor } from '@tiptap/react';
import { FileText, Image as ImageIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTronAffordability } from '../../../hooks/useTronAffordability';
import { imageFilesFrom } from '../../../utils/askImage';
import { NewPostFactiii } from '../../factiiis/NewPostFactiii';
import FormatTools from '../FormatTools';
import { PostInput } from '../LazyPostInput';
import { TOOLBAR_ICON, toolbarButton } from '../toolbarButton';
import UploadGalleryView from '../UploadGalleryView';
import CrossPostIndicator from './CrossPostIndicator';
import { DefaultFactiiiButtons } from './DefaultFactiiiButtons';
import { useNewPostContext } from './NewPostContext';
type Props = {
  extraContent?: ReactNode;
  avatar?: ReactNode;
};

// The editor scrolls inside this cap. Without it a long post grows past the
// modal's 90vh shell, which clips the toolbar and the Post button with it.
const EDITOR_SCROLL = 'w-full min-w-0 max-h-[40vh] overflow-y-auto';
export const NewPostInputSection = t0 => {
  const $ = _c(94);
  const {
    extraContent,
    avatar
  } = t0;
  const {
    title,
    setTitle,
    content,
    setContent,
    contentTooLong,
    rows,
    label,
    uploads,
    uploadingKeys,
    pendingFiles,
    removeAttachmentHandler,
    cameraPhotos,
    removeCameraPhotoHandler,
    factiiiOpen,
    setFactiiiOpen,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    requirementsMet,
    initialLocation,
    onDraftsClick,
    onFilesSelected,
    submitHandler,
    submitDisabled,
    buttonLoading,
    submitButtonText,
    pinnedFactiii,
    queryKey,
    postId,
    isPrivate,
    isOpened,
    toolbarPt: t1,
    isBoardPost
  } = useNewPostContext();
  const toolbarPt = t1 === undefined ? "pt-1" : t1;
  const {
    currentUser
  } = useUser();
  const fileInputRef = useRef(null);
  const [editor, setEditor] = useState(null);
  const costBreakdown = isPrivate ? {
    tagEditFee: 0,
    botCost: 0,
    total: 0
  } : calculateNewPostTronCostBreakdown(myPostFactiiiCounts);
  const {
    insufficientTrons,
    poolCoversRemainder
  } = useTronAffordability({
    tagEditFee: costBreakdown.tagEditFee,
    botCost: costBreakdown.botCost,
    awardCost: 0
  });
  let t2;
  if ($[0] !== onFilesSelected) {
    t2 = e => {
      const files = imageFilesFrom(e.clipboardData);
      if (files.length > 0) {
        e.preventDefault();
        onFilesSelected(files);
      }
    };
    $[0] = onFilesSelected;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handlePaste = t2;
  let t3;
  if ($[2] !== onFilesSelected) {
    t3 = e_0 => {
      const files_0 = Array.from(e_0.target.files || []);
      if (files_0.length > 0) {
        onFilesSelected(files_0);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    $[2] = onFilesSelected;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleFileChange = t3;
  let t4;
  if ($[4] !== avatar) {
    t4 = avatar && <div className="mb-2">{avatar}</div>;
    $[4] = avatar;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== content || $[7] !== isBoardPost || $[8] !== label || $[9] !== rows || $[10] !== setContent || $[11] !== setTitle || $[12] !== title) {
    t5 = isBoardPost ? <div className="space-y-3"><div><p className="mb-1.5 text-xs font-emphasis text-textSecondary">Title</p><input type="text" value={title} onChange={e_1 => setTitle(e_1.target.value)} placeholder="Enter a title for this card..." maxLength={255} autoFocus={true} className="w-full rounded-lg border border-ds-border-standard bg-background-secondary px-3 py-2.5 text-base font-emphasis text-textPrimary outline-none transition-colors duration-200 placeholder:text-textSecondary/40 focus:border-primary" /></div><div><p className="mb-1.5 text-xs font-emphasis text-textSecondary">Description{" "}<span className="font-normal text-textSecondary/50">(optional)</span></p><div className={`${EDITOR_SCROLL} rounded-lg border border-ds-border-standard bg-background-secondary px-1 transition-colors duration-200 focus-within:border-primary`}><PostInput setContent={setContent} content={content} rows={3} label="Add more detail..." onEditor={setEditor} /></div></div></div> : <div className={EDITOR_SCROLL}><PostInput setContent={setContent} content={content} rows={rows} label={label} onEditor={setEditor} /></div>;
    $[6] = content;
    $[7] = isBoardPost;
    $[8] = label;
    $[9] = rows;
    $[10] = setContent;
    $[11] = setTitle;
    $[12] = title;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== content || $[15] !== contentTooLong || $[16] !== extraContent) {
    t6 = extraContent ?? (contentTooLong && <p className="px-1 text-start text-sm text-destructive">Content must be below 5,000 characters. ({content.length.toLocaleString()}/5,000)</p>);
    $[14] = content;
    $[15] = contentTooLong;
    $[16] = extraContent;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== cameraPhotos || $[19] !== pendingFiles || $[20] !== removeAttachmentHandler || $[21] !== removeCameraPhotoHandler || $[22] !== uploadingKeys || $[23] !== uploads) {
    t7 = (uploads.length > 0 || cameraPhotos.length > 0 || pendingFiles.length > 0) && <UploadGalleryView uploads={uploads} removeAttachmentHandler={removeAttachmentHandler} cameraPhotos={cameraPhotos} removeCameraPhotoHandler={removeCameraPhotoHandler} uploadingKeys={uploadingKeys} pendingFiles={pendingFiles} />;
    $[18] = cameraPhotos;
    $[19] = pendingFiles;
    $[20] = removeAttachmentHandler;
    $[21] = removeCameraPhotoHandler;
    $[22] = uploadingKeys;
    $[23] = uploads;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== handleFileChange) {
    t8 = <input ref={fileInputRef} type="file" multiple={true} accept={FILE_INPUT_ACCEPT} onChange={handleFileChange} className="hidden" />;
    $[25] = handleFileChange;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = `flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-2 px-1 ${toolbarPt} pb-2`;
  let t10;
  let t11;
  if ($[27] !== editor) {
    t10 = <FormatTools editor={editor} />;
    t11 = editor && <span className="mx-0.5 h-5 w-px shrink-0 bg-ds-border-standard" />;
    $[27] = editor;
    $[28] = t10;
    $[29] = t11;
  } else {
    t10 = $[28];
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10 || $[31] !== t11) {
    t12 = <div className="flex grow items-center justify-between gap-0.5">{t10}{t11}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== factiiiOpen) {
    t13 = toolbarButton(factiiiOpen);
    $[33] = factiiiOpen;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== setFactiiiOpen) {
    t14 = () => setFactiiiOpen(_temp);
    $[35] = setFactiiiOpen;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Logo height={TOOLBAR_ICON} rounded={true} color="currentColor" bgColor="transparent" />;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t13 || $[39] !== t14) {
    t16 = <button type="button" title="Close Factiiis" className={t13} onClick={t14}>{t15}</button>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== initialLocation || $[42] !== myPostFactiiiCounts || $[43] !== requirementsMet || $[44] !== setMyPostFactiiiCounts) {
    t17 = <DefaultFactiiiButtons myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={_temp2} setPolitics={_temp3} requirementsMet={requirementsMet} initialLocation={initialLocation} />;
    $[41] = initialLocation;
    $[42] = myPostFactiiiCounts;
    $[43] = requirementsMet;
    $[44] = setMyPostFactiiiCounts;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <button type="button" title="Upload Media" className={toolbarButton()} onClick={() => fileInputRef.current?.click()}><ImageIcon size={TOOLBAR_ICON} /></button>;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = toolbarButton();
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FileText size={TOOLBAR_ICON} />;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== onDraftsClick) {
    t21 = <button type="button" title="Drafts" className={t19} onClick={onDraftsClick}>{t20}</button>;
    $[49] = onDraftsClick;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== currentUser.isAuthenticated) {
    t22 = currentUser.isAuthenticated && <CrossPostIndicator />;
    $[51] = currentUser.isAuthenticated;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== buttonLoading || $[54] !== submitButtonText) {
    t23 = buttonLoading ? <div className="loader" /> : submitButtonText;
    $[53] = buttonLoading;
    $[54] = submitButtonText;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== submitDisabled || $[57] !== submitHandler || $[58] !== t23) {
    t24 = <button type="button" className="ml-auto flex min-w-[72px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50" onClick={submitHandler} disabled={submitDisabled}>{t23}</button>;
    $[56] = submitDisabled;
    $[57] = submitHandler;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t16 || $[61] !== t17 || $[62] !== t21 || $[63] !== t22 || $[64] !== t24) {
    t25 = <div className="flex min-w-0 grow items-center justify-between gap-0.5">{t16}{t17}{t18}{t21}{t22}{t24}</div>;
    $[60] = t16;
    $[61] = t17;
    $[62] = t21;
    $[63] = t22;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== t12 || $[67] !== t25 || $[68] !== t9) {
    t26 = <div className={t9}>{t12}{t25}</div>;
    $[66] = t12;
    $[67] = t25;
    $[68] = t9;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  const t27 = factiiiOpen && costBreakdown.total > 0 && <div className={`flex items-center justify-between gap-2 px-1 py-2 text-xs ${insufficientTrons ? "text-destructive" : "text-textSecondary"}`}><span>Cost: <strong>{costBreakdown.total.toLocaleString()}</strong> trons</span><span className="truncate text-right">Balance:{" "}<strong>{currentUser.tronsBalance.toLocaleString()}</strong>{insufficientTrons && " \u2014 insufficient"}{poolCoversRemainder && <span className="text-primary"> — pool covers rest</span>}</span></div>;
  const t28 = factiiiOpen ? "1fr" : "0fr";
  let t29;
  if ($[70] !== t28) {
    t29 = {
      gridTemplateRows: t28
    };
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== isOpened || $[73] !== isPrivate || $[74] !== myPostFactiiiCounts || $[75] !== pinnedFactiii || $[76] !== postId || $[77] !== queryKey || $[78] !== requirementsMet || $[79] !== setMyPostFactiiiCounts) {
    t30 = <div className="min-h-0 overflow-hidden"><div className="max-h-[45vh] overflow-y-auto"><NewPostFactiii requirementsMet={requirementsMet} postId={postId} pinnedFactiii={pinnedFactiii} myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} setNsfw={_temp4} setPolitics={_temp5} queryKey={queryKey} isPrivate={isPrivate} isOpened={isOpened} costInFooter={true} /></div></div>;
    $[72] = isOpened;
    $[73] = isPrivate;
    $[74] = myPostFactiiiCounts;
    $[75] = pinnedFactiii;
    $[76] = postId;
    $[77] = queryKey;
    $[78] = requirementsMet;
    $[79] = setMyPostFactiiiCounts;
    $[80] = t30;
  } else {
    t30 = $[80];
  }
  let t31;
  if ($[81] !== t29 || $[82] !== t30) {
    t31 = <div className="grid transition-[grid-template-rows] duration-300 ease-out" style={t29}>{t30}</div>;
    $[81] = t29;
    $[82] = t30;
    $[83] = t31;
  } else {
    t31 = $[83];
  }
  let t32;
  if ($[84] !== handlePaste || $[85] !== t26 || $[86] !== t27 || $[87] !== t31 || $[88] !== t4 || $[89] !== t5 || $[90] !== t6 || $[91] !== t7 || $[92] !== t8) {
    t32 = <div onPaste={handlePaste}>{t4}{t5}{t6}{t7}{t8}{t26}{t27}{t31}</div>;
    $[84] = handlePaste;
    $[85] = t26;
    $[86] = t27;
    $[87] = t31;
    $[88] = t4;
    $[89] = t5;
    $[90] = t6;
    $[91] = t7;
    $[92] = t8;
    $[93] = t32;
  } else {
    t32 = $[93];
  }
  return t32;
};
function _temp(prev) {
  return !prev;
}
function _temp2() {}
function _temp3() {}
function _temp4() {}
function _temp5() {}